import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { HttpInterceptorFn, provideHttpClient, withInterceptors } from '@angular/common/http';
import { TitleStrategy, provideRouter, withComponentInputBinding } from '@angular/router';
import { TranslateLoader, provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { DateAdapter, MAT_DATE_FORMATS, MAT_RIPPLE_GLOBAL_OPTIONS } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatDatepickerIntl } from '@angular/material/datepicker';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { ApiTranslateLoader } from '@core/i18n/api-translate.loader/api-translate.loader';
import { APP_DATE_FORMATS, AppDateAdapter } from '@core/i18n/app-date-adapter/app-date-adapter';
import { TranslatedDatepickerIntl } from '@core/i18n/translated-datepicker-intl';
import { TranslatedPaginatorIntl } from '@core/i18n/translated-paginator-intl';
import { environment } from '@env/environment';
import { loaderInterceptor } from '@core/http/interceptors/loader.interceptor';
import { apiErrorInterceptor } from '@core/http/interceptors/api-error.interceptor/api-error.interceptor';
import { authTokenInterceptor } from '@core/http/interceptors/auth-token.interceptor';
import { StartupService } from '@core/startup/startup.service/startup.service';
import { TranslatedTitleStrategy } from '@core/i18n/translated-title.strategy';

import { routes } from './app.routes';

/**
 * Interceptor order matters:
 * loader (outermost, spans retries) -> error (sees only unrecoverable errors)
 * -> auth token (innermost, handles 401 + refresh).
 */
const interceptors: HttpInterceptorFn[] = [
  loaderInterceptor,
  apiErrorInterceptor,
  authTokenInterceptor,
];

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(withInterceptors(interceptors)),
    provideTranslateService({
      // Translations come from the backend by default; local files are a
      // development convenience behind the useLocalTranslations flag.
      loader: environment.useLocalTranslations
        ? provideTranslateHttpLoader({ prefix: '/i18n/', suffix: '.json' })
        : { provide: TranslateLoader, useClass: ApiTranslateLoader },
      fallbackLang: environment.defaultLanguage,
    }),
    // Dates: app-wide dd-MM-yyyy input format, 24h clock (environment.timeFormat),
    // calendar locale + labels following the active language.
    /*
     * Form fields app-wide. jmdi puts the label ABOVE the control as its own
     * element, so the wrappers render `app-field-label` and no `<mat-label>` at
     * all (§7.1) — with no Material label there is nothing to float, and the
     * outline stays unbroken instead of being notched.
     *
     * The subscript is `dynamic` because the wrappers bring their own
     * `app-field-subscript`; Material's built-in one would only reserve a
     * second, empty row underneath.
     */
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline', subscriptSizing: 'dynamic' },
    },
    /*
     * No ripple, matching jmdi. PrimeNG's ripple is opt-in (`ripple: true` in
     * the PrimeNG config) and legacy never turns it on — the one `Ripple` import
     * in `faq-list.component.ts` is dead weight, the effect never runs. Material
     * ripples by default, so leaving it on would ADD an animation jmdi does not
     * have. This only disables the ripple; hover/focus state layers and focus
     * rings are separate and stay, so keyboard focus remains visible (D21).
     */
    { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: { disabled: true } },
    /*
     * Nakładki CDK BEZ `popover`. Od CDK 22 każda nakładka domyślnie dostaje
     * `popover="manual"` i jest pokazywana przez `showPopover()`, czyli trafia
     * do **warstwy wierzchniej** przeglądarki (top layer). Tam nie sięga żaden
     * `z-index`: element spoza tej warstwy nie może narysować się nad nią,
     * choćby miał tysiąc razy wyższą wartość. Skutek: pełnoekranowy loader
     * (`--app-z-loader: 1100`) chował się POD otwartym oknem, mimo że nasza
     * skala z-index stawia go nad oknami (`--app-z-modal: 1000`).
     *
     * Wyłączenie przywraca stare zachowanie — nakładki zostają w
     * `.cdk-overlay-container` z `z-index: 1000` i skala znowu rozstrzyga.
     * Alternatywa (wepchnięcie loadera też do warstwy wierzchniej) dawałaby
     * kolejność zależną od tego, co weszło tam jako ostatnie, więc loader
     * trwający już przed otwarciem okna i tak zostałby pod nim.
     */
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
    { provide: DateAdapter, useClass: AppDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: APP_DATE_FORMATS },
    { provide: MatDatepickerIntl, useClass: TranslatedDatepickerIntl },
    { provide: MatPaginatorIntl, useClass: TranslatedPaginatorIntl },
    provideAppInitializer(() => inject(StartupService).initialize()),
    { provide: TitleStrategy, useClass: TranslatedTitleStrategy },
  ],
};
